import React, { useEffect, useRef } from 'react';
import './Chart.css';
import { translations } from '../translations';

const Chart = ({ frequencies, language }) => {
  const canvasRef = useRef(null);
  const t = translations[language];

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Background
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, width, height);

    const padding = 60;
    const chartWidth = width - 2 * padding;
    const chartHeight = height - 2 * padding;

    // Find maximum frequency
    const maxFreq = Math.max(...Object.values(frequencies));
    const maxFreqDisplay = Math.max(maxFreq, 50); // Show at least 50

    // Find top 3 values
    const sortedFreqs = Object.entries(frequencies)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([key]) => parseInt(key));

    // X axis (sum values)
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding, height - padding);
    ctx.lineTo(width - padding, height - padding);
    ctx.stroke();

    // Y axis
    ctx.beginPath();
    ctx.moveTo(padding, padding);
    ctx.lineTo(padding, height - padding);
    ctx.stroke();

    // Draw bars
    const barWidth = chartWidth / 9 * 0.8;
    const barSpacing = chartWidth / 9 * 0.2;

    for (let i = 2; i <= 10; i++) {
      const x = padding + (i - 2) * (chartWidth / 8) + barSpacing / 2;
      const barHeight = (frequencies[i] / maxFreqDisplay) * chartHeight;
      const y = height - padding - barHeight;

      // Bar color (special colors for top 3 values)
      if (sortedFreqs[0] === i) {
        ctx.fillStyle = '#ffeb3b'; // Highest: Yellow
      } else if (sortedFreqs[1] === i) {
        ctx.fillStyle = '#f69a2c'; // 2nd highest: Orange
      } else if (sortedFreqs[2] === i) {
        ctx.fillStyle = '#f65e0a'; // 3rd highest: Blue
      } else {
        ctx.fillStyle = '#8d6e63'; // Others: Brown
      }

      ctx.fillRect(x, y, barWidth, barHeight);

      // Value on bar
      if (frequencies[i] > 0) {
        ctx.fillStyle = '#333';
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(frequencies[i].toString(), x + barWidth / 2, y - 5);
      }
    }

    // X axis labels (aligned to bar centers)
    ctx.fillStyle = '#333';
    ctx.font = '14px Arial';
    ctx.textAlign = 'center';
    for (let i = 2; i <= 10; i++) {
      const x = padding + (i - 2) * (chartWidth / 8) + barSpacing / 2 + barWidth / 2;
      // Use smaller font for number 10
      if (i === 10) {
        ctx.font = '12px Arial';
      } else {
        ctx.font = '14px Arial';
      }
      ctx.fillText(i.toString(), x, height - padding + 20);
    }

    // Y axis labels
    ctx.textAlign = 'right';
    for (let i = 0; i <= 5; i++) {
      const y = height - padding - (i * chartHeight / 5);
      const value = Math.round((i * maxFreqDisplay) / 5);
      ctx.fillText(value.toString(), padding - 10, y + 5);

      // Horizontal lines
      if (i > 0) {
        ctx.strokeStyle = '#ddd';
        ctx.lineWidth = 0.5;
        ctx.zIndex = -999;
        ctx.beginPath();
        ctx.moveTo(padding, y);
        ctx.lineTo(width - padding, y);
        ctx.stroke();
      }
    }

    // Axis titles
    ctx.fillStyle = '#333';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(t.total, width / 2, height - 10);

    ctx.save();
    ctx.translate(20, height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText(t.frequency, 0, 0);
    ctx.restore();
  }, [frequencies, language, t]);

  return (
    <div className="chart-container">
      <canvas 
        ref={canvasRef} 
        width={800} 
        height={400}
        className="chart-canvas"
      />
    </div>
  );
};

export default Chart; 